import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isNativePlatform } from '../../native/platform';
import EventTicket from '../../components/EventTicket';
import MobileTicketView from '../../components/MobileTicketView';
import EmptyState from '../../components/attendee/EmptyState';
import LoadingState from '../../components/attendee/LoadingState';
import { formatDate } from '../../utils/attendeeUtils';

const AttendeeTicketsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState('upcoming');
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    if (user) fetchTickets();
  }, [user]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('event_registrations')
        .select('*, events(id, title, event, start_date, end_date, start, end, venue_name, venue, city, location, image_url, status, address, latitude, longitude, organization, org)')
        .eq('attendee_email', user.email)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTickets(data || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const eventDate = new Date(ticket.events?.end_date || ticket.events?.end || ticket.events?.start_date || ticket.events?.start);
    const now = new Date();
    if (filter === 'upcoming') return eventDate >= now && ticket.status !== 'cancelled';
    if (filter === 'past') return eventDate < now;
    return true;
  });

  const handleTicketClick = (ticket) => {
    if (isNativePlatform()) {
      setSelectedTicket(ticket);
    } else {
      setSelectedTicket(ticket);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse mb-6" />
        <LoadingState count={3} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          My Tickets
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          View and download your event tickets with QR codes.
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6">
        {['upcoming', 'past', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
              filter === f
                ? 'bg-primary text-white'
                : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Tickets list */}
      {filteredTickets.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTickets.map(ticket => {
            const event = ticket.events;
            const ticketCode = ticket.ticket_code || ticket.id?.slice(0, 8).toUpperCase();
            const isCancelled = ticket.status === 'cancelled';
            const isPaid = ticket.payment_status === 'paid';

            return (
              <div
                key={ticket.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleTicketClick(ticket)}
              >
                {/* Event image header */}
                {event?.image_url && (
                  <div className="h-24 overflow-hidden">
                    <img src={event.image_url} alt={event.title || event.event} className="w-full h-full object-cover" />
                  </div>
                )}

                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                    {event?.title || event?.event || 'Event'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {formatDate(event?.start_date || event?.start)}
                  </p>
                  {event?.venue_name && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                      {event.venue_name}
                    </p>
                  )}

                  {/* Ticket info */}
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500 dark:text-gray-400">Ticket Code</span>
                      <span className="font-mono font-medium text-gray-900 dark:text-white">{ticketCode}</span>
                    </div>
                    {ticket.ticket_type && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 dark:text-gray-400">Type</span>
                        <span className="text-gray-900 dark:text-white">{ticket.ticket_type}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500 dark:text-gray-400">Attendee</span>
                      <span className="text-gray-900 dark:text-white truncate ml-2">{ticket.attendee_name || user?.email}</span>
                    </div>
                  </div>

                  {/* Status badges */}
                  <div className="flex items-center gap-2 mt-3">
                    {isCancelled ? (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300">
                        Cancelled
                      </span>
                    ) : (
                      <>
                        {isPaid ? (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300">
                            Paid
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300">
                            Payment Pending
                          </span>
                        )}
                        {ticket.checked_in_at && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-teal-100 dark:bg-teal-900/50 text-teal-800 dark:text-teal-300">
                            Checked In
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {/* View QR button */}
                  {!isCancelled && (
                    <button
                      className="w-full mt-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 3h-3m3 0v3m3-3h3M3 16.5A2.5 2.5 0 015.5 14h6.5v6.5a2.5 2.5 0 01-2.5 2.5h-4A2.5 2.5 0 013 20.5v-4z" />
                      </svg>
                      View QR
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title={filter === 'upcoming' ? 'No upcoming tickets' : filter === 'past' ? 'No past tickets' : 'No tickets'}
          message={filter === 'upcoming' ? "Register for an event to get your ticket." : "Your tickets will appear here."}
          ctaText={filter === 'upcoming' ? 'Browse Events' : undefined}
          ctaLink={filter === 'upcoming' ? '/events' : undefined}
        />
      )}

      {/* Ticket Modal (web) */}
      {selectedTicket && !isNativePlatform() && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setSelectedTicket(null)}
        >
          <div className="max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setSelectedTicket(null)}
                className="text-white/80 hover:text-white"
                aria-label="Close ticket"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <EventTicket registration={selectedTicket} event={selectedTicket.events} showDownload={true} />
          </div>
        </div>
      )}

      {/* Mobile full-screen ticket view */}
      {selectedTicket && isNativePlatform() && (
        <MobileTicketView
          tickets={filteredTickets}
          initialIndex={filteredTickets.findIndex(t => t.id === selectedTicket.id)}
          onClose={() => setSelectedTicket(null)}
        />
      )}
    </div>
  );
};

export default AttendeeTicketsPage;
